import React, { useState, useEffect } from 'react';
import './App.css';

const processAuditData = (auditData) => {
  if (!auditData || !Array.isArray(auditData.data)) {
    console.error("Invalid audit data structure received from API", auditData);
    return [];
  }
  const processedData = auditData.data.filter(data => data.endpoint_url === '[AGGREGATED]');
  processedData.forEach(item => {
    try {
      // Ensure processed_response is parsed if it's a string
      item.processed_response_json = typeof item.processed_response === 'string' ? JSON.parse(item.processed_response) : item.processed_response;
      if (item.processed_response_json && item.processed_response_json.data) {
        delete item.processed_response_json.data.heatmap;
        delete item.processed_response_json.data.lineups_events;
        delete item.processed_response_json.data.players_statistics;
        delete item.processed_response_json.data.scraper_time;
      }
    } catch (e) {
      console.error('Error parsing processed_response:', e, item.processed_response);
      item.processed_response_json = {}; // Assign empty object to prevent further errors
    }
  });
  console.log('processedData', processedData)
  return processedData;

}

/**
 * Compares two JSON objects and returns the differences.
 * This function performs a deep diff on changed objects to only show the most granular change.
 * @param {Object} prevDetails - The previous JSON object.
 * @param {Object} currentDetails - The current JSON object.
 * @returns {Object} An object containing added, removed, and changed key-value pairs.
 */
function getJsonDiff(prevDetails, currentDetails) {
  if (!prevDetails) {
    return { added: currentDetails };
  }

  const isObject = (obj) => obj && typeof obj === 'object' && !Array.isArray(obj);
  const isArray = (obj) => Array.isArray(obj);

  /**
   * Recursively finds the exact changes between two objects or arrays.
   * @param {*} prev - The previous value.
   * @param {*} current - The current value.
   * @returns {Object|null} An object with `from` and `to` properties showing the diff, or null if no changes.
   */
  const getDeepChanges = (prev, current) => {
    // Handle primitive type changes
    if (!isObject(prev) && !isArray(prev) || !isObject(current) && !isArray(current)) {
      if (prev !== current) {
        return { from: prev, to: current };
      }
      return null; // No change for primitives
    }

    // Handle array changes
    if (isArray(prev) && isArray(current)) {
      const fromArray = [];
      const toArray = [];
      const addedItems = [];
      const removedItems = [];
      let hasChanges = false;

      // Find added and removed items
      const prevSet = new Set(prev.map(item => JSON.stringify(item)));
      const currentSet = new Set(current.map(item => JSON.stringify(item)));

      for (const item of current) {
        if (!prevSet.has(JSON.stringify(item))) {
          addedItems.push(item);
          hasChanges = true;
        }
      }

      for (const item of prev) {
        if (!currentSet.has(JSON.stringify(item))) {
          removedItems.push(item);
          hasChanges = true;
        }
      }

      // If only additions or removals, report accordingly
      if (addedItems.length > 0 && removedItems.length === 0) {
        return { added: addedItems }; // Special case for array additions
      }
      if (removedItems.length > 0 && addedItems.length === 0) {
        return { removed: removedItems }; // Special case for array removals
      }

      // If there are both additions/removals or modifications within arrays,
      // we'll treat it as a 'changed' array with 'from' and 'to' representing the overall state.
      // Or, if items are reordered, we'll also see this as a change.
      if (addedItems.length > 0 || removedItems.length > 0) {
        return { from: prev, to: current };
      }

      // Check for deep changes within common elements (e.g., array of objects)
      const maxLength = Math.max(prev.length, current.length);
      for (let i = 0; i < maxLength; i++) {
        const prevItem = prev[i];
        const currentItem = current[i];

        if (prevItem !== undefined && currentItem !== undefined) {
          const nestedChanges = getDeepChanges(prevItem, currentItem);
          if (nestedChanges) {
            fromArray[i] = nestedChanges.from;
            toArray[i] = nestedChanges.to;
            hasChanges = true;
          } else {
            // If no nested changes, keep the original item (or undefined if out of bounds)
            fromArray[i] = prevItem;
            toArray[i] = currentItem;
          }
        } else if (prevItem !== undefined) {
          fromArray[i] = prevItem; // Item removed by index
          hasChanges = true;
        } else if (currentItem !== undefined) {
          toArray[i] = currentItem; // Item added by index
          hasChanges = true;
        }
      }

      return hasChanges ? { from: fromArray, to: toArray } : null;
    }

    // Handle object changes
    else if (isObject(prev) && isObject(current)) {
      const fromDiff = {};
      const toDiff = {};
      let hasChanges = false;
      const allKeys = new Set([...Object.keys(prev), ...Object.keys(current)]);

      for (const key of allKeys) {
        const prevValue = prev[key];
        const currentValue = current[key];
        const prevExists = prev.hasOwnProperty(key);
        const currentExists = current.hasOwnProperty(key);

        if (prevExists && currentExists) {
          // If values are objects or arrays, recurse
          if (isObject(prevValue) || isArray(prevValue) || isObject(currentValue) || isArray(currentValue)) {
            const nestedChanges = getDeepChanges(prevValue, currentValue);
            if (nestedChanges) {
              if (nestedChanges.added) { // Propagate array additions up
                if (!toDiff[key]) toDiff[key] = {};
                toDiff[key].added = nestedChanges.added;
                hasChanges = true;
              } else if (nestedChanges.removed) { // Propagate array removals up
                if (!fromDiff[key]) fromDiff[key] = {};
                fromDiff[key].removed = nestedChanges.removed;
                hasChanges = true;
              } else {
                fromDiff[key] = nestedChanges.from;
                toDiff[key] = nestedChanges.to;
                hasChanges = true;
              }
            }
          } else if (JSON.stringify(prevValue) !== JSON.stringify(currentValue)) {
            fromDiff[key] = prevValue;
            toDiff[key] = currentValue;
            hasChanges = true;
          }
        } else if (currentExists) { // Key was added in the nested object
          toDiff[key] = currentValue;
          hasChanges = true;
        } else { // Key was removed from the nested object
          fromDiff[key] = prevValue;
          hasChanges = true;
        }
      }

      return hasChanges ? { from: fromDiff, to: toDiff } : null;
    }

    // If types are different or one is null/undefined and the other isn't
    if (prev !== current) {
      return { from: prev, to: current };
    }

    return null;
  };

  const diff = {};
  const allKeys = new Set([...Object.keys(prevDetails), ...Object.keys(currentDetails)]);

  for (const key of allKeys) {
    const prevValue = prevDetails[key];
    const currentValue = currentDetails[key];

    if (!prevDetails.hasOwnProperty(key)) {
      if (!diff.added) diff.added = {};
      diff.added[key] = currentValue;
    } else if (!currentDetails.hasOwnProperty(key)) {
      if (!diff.removed) diff.removed = {};
      diff.removed[key] = prevValue;
    } else if (JSON.stringify(prevValue) !== JSON.stringify(currentValue)) {
      const deepChanges = getDeepChanges(prevValue, currentValue);
      if (deepChanges) {
        if (deepChanges.added) {
          if (!diff.added) diff.added = {};
          diff.added[key] = deepChanges.added;
        } else if (deepChanges.removed) {
          if (!diff.removed) diff.removed = {};
          diff.removed[key] = deepChanges.removed;
        } else {
          // This ensures that if a nested object is entirely new, it's categorized as 'added'
          // rather than a 'changed' object with an empty 'from' property.
          if (isObject(deepChanges.from) && Object.keys(deepChanges.from).length === 0 &&
            isObject(deepChanges.to) && Object.keys(deepChanges.to).length > 0) {
            if (!diff.added) diff.added = {};
            diff.added[key] = deepChanges.to;
          } else {
            if (!diff.changed) diff.changed = {};
            diff.changed[key] = deepChanges;
          }
        }
      }
    }
  }
  return diff;
}

function DiffViewer({ data }) {
  if (!data || Object.keys(data).length === 0) {
    return null;
  }
  return (
    <div className="diff-viewer">
      <pre>{JSON.stringify(data, null, 2)}</pre>
    </div>
  );
}

function App() {
  const [data, setData] = useState([]);
  const [footstatsData, setFootstatsData] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [homeTeam, setHomeTeam] = useState('');
  const [awayTeam, setAwayTeam] = useState('');
  const [extrabetId, setExtrabetId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const defaultLimit = 20; // Default limit for pagination

  const fetchMatchDataFromLambda = async (extrabetId) => {
    try {
      const response = await fetch(`https://lambda-dev.extratime.cloud/api/v2/match-odds/${extrabetId}/?source=footstats`)

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const matchData = await response.json();
      setAwayTeam(matchData[extrabetId].meta_data.team2);
      setHomeTeam(matchData[extrabetId].meta_data.team1);

    } catch (error) {
      console.error("Failed to fetch match data:", error);
      throw error;
    }
    }

  const fetchAndProcessData = async () => {
      if (!extrabetId) {
        setError("Please enter an Extrabet ID.");
        return;
      }
      setIsLoading(true);
      setError(null);
      setData([]);
      setFootstatsData([]);
      fetchMatchDataFromLambda(extrabetId);
      let allExtratimeData = [];
      let allFootstatsData = [];
      let currentPage = 1;
      let hasNextPage = true;

      try {
        while (hasNextPage) {
          const response = await fetch(
            `https://lambda-dev.extratime.cloud/api/v2/audit-logs?extrabet_id=${extrabetId}&page=${currentPage}&limit=${defaultLimit}`,
            {
              headers: {
                'x-api-key': 'extrabet-audit-logs-2025'
              }
            }
          );

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const auditDataFromApi = await response.json();

          // Process Extratime Data
          const processedExtratimePageData = processAuditData(auditDataFromApi);
          allExtratimeData = allExtratimeData.concat(processedExtratimePageData);

          // Process Footstats Data
          const footstatsPage = auditDataFromApi.data.filter(data => data.endpoint_url.includes('teams/scout'));
          footstatsPage.forEach(item => {
            try {
              item.processed_response_json = typeof item.raw_response === 'string' ? JSON.parse(item.raw_response) : item.raw_response;
            } catch (e) {
              console.error('Error parsing footstats raw_response:', e, item.raw_response);
              item.processed_response_json = {};
            }
          });
          allFootstatsData = allFootstatsData.concat(footstatsPage);

          // Check pagination for the next iteration
          if (auditDataFromApi.pagination && auditDataFromApi.pagination.has_next) {
            currentPage++;
          } else {
            hasNextPage = false;
          }
        }

        // Sort data by timestamp in ascending (chronological) order
        console.log(allExtratimeData[0])
        allExtratimeData.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        allFootstatsData.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        console.log(allExtratimeData[0])

        setData(allExtratimeData);
        setFootstatsData(allFootstatsData);
        console.log('All processed Extratime Data (sorted):', allExtratimeData);
        console.log('All Footstats Data (sorted):', allFootstatsData);
      } catch (error) {
        console.error("Failed to fetch or process audit logs:", error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    // Initial fetch when component mounts (optional, uncomment if you want data on load)
    // useEffect(() => {
    //   fetchAndProcessData();
    // }, []);

    const toggleRowExpansion = (id) => {
      setExpandedRows(prev => ({
        ...prev,
        [id]: !prev[id]
      }));
    };

    return (
      <div className="App">
        <header className="App-header">
          <h1>Log Audit Dashboard</h1>
          <div className="input-container">
            <label htmlFor="extrabet-id-input">Extrabet ID:</label>
            <input
              id="extrabet-id-input"
              type="text"
              value={extrabetId}
              onChange={(e) => setExtrabetId(e.target.value)}
              placeholder="Enter Extrabet ID"
              // style={{ width: '15vw', marginLeft: '10px', marginRight: '10px' }}
            />
            <button onClick={fetchAndProcessData} disabled={isLoading}>
              {isLoading ? 'Loading...' : 'Load Data'}
            </button>
            <div style={{ marginLeft: '50px', width: '100%', textWrapMode: 'nowrap', }}>{ `HomeTeam: ${homeTeam}` }</div>
            <div style={{ marginLeft: '50px', width: '100%', textWrapMode: 'nowrap' }}>{ `AwayTeam: ${awayTeam}` }</div>
          </div>
        </header>
        <main>
          {isLoading && <div style={{ fontSize: '2em', margin: '30px' }} className="loading-indicator">Fetching all data, please wait...</div>}
          {error && <div className="error-message">Error: {error}</div>}
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Timestamp</th>
                  <th>Footstats Statistics Data (JSON)</th>
                  <th>Extratime Statistics Data (JSON)</th>
                  <th>Added</th>
                  <th>Changed From</th>
                  <th>Changed To</th>
                  <th>Removed</th>
                </tr>
              </thead>
              <tbody>
                {data.map((item, index) => {
                  const prevDetails = index > 0 ? data[index - 1].processed_response_json : null;
                  const diff = getJsonDiff(prevDetails, item.processed_response_json);
                  const isExpanded = !!expandedRows[item.id];
                  if (index === 0) // Skip the first row as it has no previous data for diffing
                    return null;
                  console.log('diff', diff);
                  console.log('diff.changed', diff.changed);
                  return (
                    <tr className="data-row" key={item.id}>
                      <td>{item.id}</td>
                      <td>{new Date(item.timestamp).toLocaleString()}</td>
                      <td className="expandable-cell" onClick={() => toggleRowExpansion(item.id)}>
                        <div className="expand-icon">{isExpanded ? '−' : '+'}</div>
                        <div className="json-content">
                          {isExpanded ? (
                            <div className="pre-wrapper">
                              <button
                                className="copy-button"
                                onClick={(e) => {
                                  e.stopPropagation(); // Prevent row from collapsing
                                  navigator.clipboard.writeText(JSON.stringify(footstatsData[index].processed_response_json, null, 2));
                                  const originalText = e.target.innerText;
                                  e.target.innerText = 'Copied!';
                                  setTimeout(() => {
                                    e.target.innerText = originalText;
                                  }, 1500);
                                }}
                              >
                                Copy
                              </button>
                              <pre>{footstatsData[index] ? JSON.stringify(footstatsData[index].processed_response_json, null, 2) : 'No data'}</pre>
                            </div>
                          ) : (
                            <code>{`{...} /* Click to expand */`}</code>
                          )}
                        </div>
                      </td>
                      <td className="expandable-cell" onClick={() => toggleRowExpansion(item.id)}>
                        <div className="expand-icon">{isExpanded ? '−' : '+'}</div>
                        <div className="json-content">
                          {isExpanded ? (
                            <div className="pre-wrapper">
                              <button
                                className="copy-button"
                                onClick={(e) => {
                                  e.stopPropagation(); // Prevent row from collapsing
                                  navigator.clipboard.writeText(JSON.stringify(item.processed_response_json, null, 2));
                                  const originalText = e.target.innerText;
                                  e.target.innerText = 'Copied!';
                                  setTimeout(() => {
                                    e.target.innerText = originalText;
                                  }, 1500);
                                }}
                              >
                                Copy
                              </button>
                              <pre>{JSON.stringify(item.processed_response_json, null, 2)}</pre>
                            </div>
                          ) : (
                            <code>{`{...} /* Click to expand */`}</code>
                          )}
                        </div>
                      </td>
                      <td>
                        <DiffViewer data={diff.added} />
                      </td>
                      <td>
                        <DiffViewer data={diff?.changed?.data.from} />
                      </td>
                      <td>
                        <DiffViewer data={diff?.changed?.data.to} />
                      </td>
                      <td>
                        <DiffViewer data={diff.removed} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </main>
      </div>
    );
  }

  export default App;